import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  AccountStatus,
  Box,
  Checkbox,
  ErrorModal,
  FormProvider,
  Inscription,
  Preloader,
  Text,
  useResponsive,
  WarningCard,
  WarningIcon,
  WarnModal,
} from '@common-fe/common-fe';
import { toNumber } from 'lodash';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import { REQUIRED_NOT_EMPTY } from '@/common/constants';
import { BackButton, Modal } from '@/components';
import AppButton from '@/components/controls/AppButton';
import useHighlightedTaxFormStore from '@/modules/investments/TaxForms/stores/useHighlightedTaxForm.store';
import Immediately from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately';
import { useGetPaymentMethods } from '@/modules/transaction/components/ReimburseMe/queries/useGetPaymentMethods.query';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import {
  PaymentMethod,
  PaymentMethodVerificationStatus,
} from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { useMyEnrollments, useOrganizationProperties } from '@/modules/transaction/hooks';
import { HSA_TYPE_KEY } from '@/modules/transaction/hooks/useMyEnrollments';
import { useAuthStore } from '@/modules/user';
import useHighlightById from '@/utils/hooks/useHighlightById';

import { ExcessContributionForm } from './ExcessContributionForm';
import { UploadProofFile } from './UploadProofFile';
import {
  ExcessContributionFormData,
  ExcessContributionFormField,
  useExcessContributionForm,
} from './useExcessContributionForm';
import { useCreateExcessContributionQuery } from './useExcessContributionWithdraw.query';
import useGetExcessContributionQuery from './useGetExcessContribution.query';

const FormWrapper = styled(Box)`
  input {
    width: 335px;
  }
`;

const getEmptyError = (amount?: string, otherAmount?: string) => {
  if (!amount) return REQUIRED_NOT_EMPTY;
  if (toNumber(amount) + toNumber(otherAmount || 0) <= 0) return 'Total must be greater than 0';
  return '';
};
const getErrors = (
  formValues: ExcessContributionFormData,
  paymentEmployeeFee?: number,
  availableHsaAccountBalance?: number
) => {
  const excessAmount = formValues[ExcessContributionFormField.excessAmount];
  const excessEarningsAmount = formValues[ExcessContributionFormField.excessEarningsAmount];
  const total =
    toNumber(excessAmount || 0) + toNumber(excessEarningsAmount || 0) + toNumber(paymentEmployeeFee || 0);
  const hasBalanceOverrunError =
    typeof availableHsaAccountBalance === 'number' && total > availableHsaAccountBalance;
  const balanceOverrunError = hasBalanceOverrunError
    ? 'The total withdraw must be less than your current HSA balance'
    : '';
  return {
    [ExcessContributionFormField.excessAmount]:
      getEmptyError(excessAmount, excessEarningsAmount) || balanceOverrunError,
    [ExcessContributionFormField.excessEarningsAmount]:
      getEmptyError(excessEarningsAmount, excessAmount) || balanceOverrunError,
  };
};

interface Props {
  onSetVisible: (visible: boolean) => void;
  isEditMode?: boolean;
  accountId?: string;
  id?: string;
}

const ExcessContributionWithdrawModal: React.FC<Props> = ({ onSetVisible, isEditMode, id, accountId }) => {
  const { isMobile } = useResponsive();
  const [createdExcessContributionId, setCreatedExcessContributionId] = useState<number>();
  const { reset, setHighlightedTaxFormId } = useHighlightedTaxFormStore();
  const { setHighlightedById } = useHighlightById({ onSetHighlightedId: setHighlightedTaxFormId, reset });
  const [isSetupPaymentMethod, setIsSetupPaymentMethod] = useState(!isEditMode);
  const { excessContribution, isLoading: isExcessContributionLoading } = useGetExcessContributionQuery(
    id,
    !isEditMode
  );
  const history = useHistory();
  const goToTaxFormsPage = useCallback(() => history.push(ROUTES.MY_DOCUMENTS_TAX_FORMS), [history]);
  const [hasSuccessModal, setHasSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { checkIsAvailablePaymentMethod, organizationProperties } = useOrganizationProperties(isEditMode);
  const { data, isLoading: isHsaAccountLoading } = useMyEnrollments(undefined, undefined, isEditMode);

  const hsaAccount = useMemo(() => {
    if (accountId) {
      return data.find(
        (item) =>
          item.id === `${accountId}` &&
          item.accountType === HSA_TYPE_KEY &&
          item.accountStatus === AccountStatus.OPEN
      );
    }
    return data.find(
      (item) => item.accountType === HSA_TYPE_KEY && item.accountStatus === AccountStatus.OPEN
    );
  }, [data, accountId]);

  const availableHsaAccountBalance = useMemo(() => hsaAccount?.available, [hsaAccount]);
  const handleSetPaymentMethods = useStore((state) => state.handleSetPaymentMethods);
  const selectedMethod = useStore((state) => state.selectedMethod);
  const activeMethod = useStore((state) => state.activeMethod);
  const hasPendingStatus = useMemo(
    () =>
      activeMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
      activeMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL,
    [activeMethod]
  );
  const handleSetActivePaymentMethod = useStore((state) => state.handleSetActivePaymentMethod);
  const handleSetActivePaymentMethodById = useStore((state) => state.handleSetActivePaymentMethodById);
  useEffect(() => {
    if (isEditMode || !excessContribution?.paymentMethodId || !activeMethod) return;
    handleSetActivePaymentMethodById(excessContribution.paymentMethodId);
    setIsSetupPaymentMethod(false);
  }, [handleSetActivePaymentMethodById, isEditMode, excessContribution, activeMethod]);
  const paymentEmployeeFee = useMemo(() => {
    const fee = organizationProperties?.allowedPaymentMethodConfigs?.find(
      (item) => item?.paymentMethodType === selectedMethod?.type
    )?.paymentEmployeeFee;
    if (fee) return toNumber(fee);
  }, [selectedMethod, organizationProperties]);
  const { t } = useTranslation();
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const { user } = useAuthStore();
  const employeeId = useMemo(() => user?.employee?.id || '', [user?.employee?.id]);
  const handleGettingPaymentMethods = useCallback(
    (paymentMethods: PaymentMethod[]) => {
      handleSetPaymentMethods(
        paymentMethods.filter((method) => method?.paymentMethodOwner?.employeeId === employeeId)
      );
    },
    [employeeId, handleSetPaymentMethods]
  );
  const { isLoading } = useGetPaymentMethods(handleGettingPaymentMethods);
  const selectedPaymentMethod = useStore((state) => state.selectedMethod);
  const isPaymentMethodAvailable = checkIsAvailablePaymentMethod(selectedPaymentMethod);

  const methods = useExcessContributionForm();
  const formValues = methods.watch();
  const errors = useMemo(
    () => getErrors(formValues, paymentEmployeeFee, availableHsaAccountBalance),
    [formValues, paymentEmployeeFee, availableHsaAccountBalance]
  );

  useEffect(() => {
    if (isEditMode) return;
    if (!excessContribution?.amount && !excessContribution?.excessEarnings) return;
    if (excessContribution?.amount) {
      methods.setValue(ExcessContributionFormField.excessAmount, excessContribution.amount);
    }
    if (excessContribution?.excessEarnings) {
      methods.setValue(ExcessContributionFormField.excessEarningsAmount, excessContribution.excessEarnings);
    }
  }, [excessContribution, isEditMode, methods]);

  useEffect(() => {
    if (!isSubmitted) return;

    if (
      methods.formState.errors[ExcessContributionFormField.excessAmount]?.message !==
      errors[ExcessContributionFormField.excessAmount]
    ) {
      methods.setError(ExcessContributionFormField.excessAmount, {
        message: errors[ExcessContributionFormField.excessAmount],
      });
    }
    if (
      methods.formState.errors[ExcessContributionFormField.excessEarningsAmount]?.message !==
      errors[ExcessContributionFormField.excessEarningsAmount]
    ) {
      methods.setError(ExcessContributionFormField.excessEarningsAmount, {
        message: errors[ExcessContributionFormField.excessEarningsAmount],
      });
    }
  }, [paymentEmployeeFee, availableHsaAccountBalance, formValues, methods, isSubmitted, errors]);

  useEffect(() => {
    if (isPaymentMethodAvailable) handleSetActivePaymentMethod();
  }, [handleSetActivePaymentMethod, isPaymentMethodAvailable]);

  const { create, isLoading: isCreating } = useCreateExcessContributionQuery(
    () => setHasSuccessModal(true),
    (error?: string) => setErrorMessage(error || '')
  );
  const handleSubmit = useCallback(async () => {
    setIsSubmitted(true);
    if (
      errors[ExcessContributionFormField.excessAmount] ||
      errors[ExcessContributionFormField.excessEarningsAmount] ||
      !selectedMethod?.id ||
      !hsaAccount?.id
    ) {
      return;
    }

    const {
      data: { excess_contribution_id },
    } = await create({
      employee_id: toNumber(employeeId),
      account_id: toNumber(hsaAccount?.id),
      amount: toNumber(formValues[ExcessContributionFormField.excessAmount]),
      excess_earnings: toNumber(formValues[ExcessContributionFormField.excessEarningsAmount]),
      payment_method_id: toNumber(selectedMethod?.id),
    });
    if (excess_contribution_id) {
      setCreatedExcessContributionId(excess_contribution_id);
      setHighlightedById(`${excess_contribution_id}`);
    }
  }, [
    setIsSubmitted,
    formValues,
    create,
    employeeId,
    selectedMethod,
    hsaAccount,
    errors,
    setHighlightedById,
  ]);

  return (
    <>
      <WarnModal
        testId="success"
        isSuccessIcon
        header="Excess contribution request was successfully submitted!"
        submitButtonNode={
          <AppButton
            testId="gotIt"
            width={isMobile ? '100%' : '200px'}
            onClick={() => {
              setHasSuccessModal(false);
              onSetVisible(false);
            }}
          >
            Got It!
          </AppButton>
        }
        cancelButtonNode={
          <AppButton
            testId="showRequest"
            buttonType="secondary"
            width={isMobile ? '100%' : '200px'}
            onClick={() => {
              if (createdExcessContributionId) {
                setHighlightedById(`${createdExcessContributionId}`);
              }
              goToTaxFormsPage();
              setHasSuccessModal(false);
              onSetVisible(false);
            }}
          >
            Show Request
          </AppButton>
        }
        visible={hasSuccessModal}
        onSetVisible={() => {
          setHasSuccessModal(false);
          onSetVisible(false);
        }}
      />
      <ErrorModal
        testId="error"
        header="Error"
        helpText={errorMessage}
        visible={Boolean(errorMessage)}
        onSetVisible={() => {
          setErrorMessage('');
          onSetVisible(false);
        }}
        buttonText="Close"
      />
      <Modal
        isClosingByClickOutsideBlocked
        testId="ExcessContributionWithdrawModal"
        visible={!hasSuccessModal && !errorMessage}
        onSetVisible={onSetVisible}
        isCloseButtonHidden={isMobile}
        isFullScreen={isMobile}
        contentMobileStyle={{ padding: 0 }}
      >
        {isMobile ? (
          <Box pad={{ horizontal: 'spacing16', vertical: 'spacing24' }}>
            <BackButton
              testId="ExcessContributionWithdrawModal"
              onClick={() => {
                onSetVisible(false);
              }}
              title="Request Excess Contribution Withdraw"
            />
          </Box>
        ) : null}
        {isMobile ? null : (
          <Box pad={{ horizontal: 'spacing24', bottom: 'spacing24' }}>
            <Text
              size="2xl"
              textAlign="center"
              weight={700}
              data-testId="ExcessContributionWithdrawModal_title_modal"
            >
              Request Excess Contribution Withdraw
            </Text>
          </Box>
        )}
        <Inscription
          size="medium"
          color="textBody"
          textAlign={isMobile ? 'start' : 'center'}
          margin={{ bottom: 'spacing24', ...(isMobile ? { horizontal: 'spacing16' } : undefined) }}
        >
          {t(
            'Excess contributions are amounts you put into your HSA beyond the IRS annual limit, which must be corrected to avoid taxes and penalties.'
          )}
        </Inscription>

        <Box
          direction="column"
          gap="spacing12"
          background="module"
          pad={isMobile ? 'spacing16' : 'spacing24'}
          round="moduleRound"
        >
          <Box round="container1Round" border={{ size: 'small', color: 'border2' }}>
            <FormWrapper background="canvas" round="container1Round">
              {isExcessContributionLoading ? (
                <Box pad="spacing24">
                  <Preloader testId="FormLoading" />
                </Box>
              ) : (
                <FormProvider {...methods}>
                  <ExcessContributionForm isEditMode={isEditMode} />
                </FormProvider>
              )}
            </FormWrapper>
          </Box>

          <Box
            data-testId="refundMethodSection"
            direction="column"
            gap="spacing12"
            round="container1Round"
            border={{ size: 'small', color: 'border2' }}
            pad={isMobile ? 'spacing16' : 'spacing24'}
            background="canvas"
          >
            <Immediately
              isTitleHidden
              isRefund
              isLoading={isLoading || isExcessContributionLoading || isSetupPaymentMethod}
              type="full"
              allowedPaymentMethodConfigs={organizationProperties?.allowedPaymentMethodConfigs}
              isViewMode={!isEditMode}
            />
          </Box>

          {!isEditMode && excessContribution?.fileUrl ? (
            <Box
              data-testId="fileSection"
              direction="column"
              pad={{
                vertical: 'spacing16',
                right: isMobile ? 'spacing16' : 'spacing24',
                left: isMobile ? 'spacing8' : 'spacing24',
              }}
              round="container1Round"
              border={{ size: 'small', color: 'border2' }}
              background="canvas"
            >
              <UploadProofFile fileUrl={excessContribution?.fileUrl} />
            </Box>
          ) : null}

          {isEditMode ? (
            <WarningCard
              containerProps={{ pad: 'spacing12', border: { color: 'transparent', size: 'small' } }}
              backgroundColor="canvas"
              testId="warningSection"
            >
              <Box
                border={{ color: 'warningBorder', size: 'small' }}
                round="container2Round"
                background="warningContainer"
                pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
              >
                <Box direction="row" align="center">
                  <WarningIcon />
                  <Text margin={{ left: 'spacing8' }} weight="bold">
                    Excess contribution correction withdrawal
                  </Text>
                </Box>

                <Box margin={{ top: 'spacing8', left: isMobile ? undefined : 'spacing32' }}>
                  <Text color="textBody">
                    By signing this form, I understand that{' '}
                    {organizationProperties?.hsaCustodianType ?? 'UMB'} will report this distribution to the
                    IRS as an excess contribution. Funds contributed in excess of your contribution limit are
                    subject to penalty and tax unless the excess and earnings are withdrawn by you prior to
                    your tax filing due date, including any extensions, for filing your Federal Income Tax
                    return.
                  </Text>
                </Box>
                <Box direction="row" gap="spacing8" align="center" margin={{ top: 'spacing16' }}>
                  <Checkbox
                    checked={rulesAccepted}
                    onChange={(val) => {
                      setRulesAccepted(val);
                    }}
                    backgroundColor="canvas"
                  />
                  <Text color="textBody">{t('I understand the above rules')}</Text>
                </Box>
              </Box>
            </WarningCard>
          ) : null}
        </Box>
        {isSubmitted && !selectedMethod?.id ? (
          <Box align="end" margin={{ top: 'spacing12' }}>
            <Text color="danger">Select a payment method</Text>
          </Box>
        ) : null}
        <Box
          data-testId="actionButtons"
          direction="row"
          gap="spacing12"
          justify={isMobile ? 'center' : 'end'}
          margin={{ top: 'spacing24', bottom: isMobile ? 'spacing24' : undefined }}
          pad={isMobile ? 'spacing24' : undefined}
          border={isMobile ? { position: 'top', color: 'border1' } : undefined}
          background={isMobile ? 'module' : undefined}
        >
          <Box flex={isMobile ? 'grow' : undefined} width={isMobile ? undefined : '160px'}>
            <AppButton
              testId="cancel"
              buttonType="secondary"
              fill={isMobile}
              width={isMobile ? undefined : '100%'}
              onClick={() => onSetVisible(false)}
            >
              {t('Cancel')}
            </AppButton>
          </Box>

          {isEditMode ? (
            <Box flex={isMobile ? 'grow' : undefined} width={isMobile ? undefined : '160px'}>
              <AppButton
                testId="submit"
                buttonType="primary"
                fill={isMobile}
                width={isMobile ? undefined : '100%'}
                disabled={
                  !rulesAccepted || isLoading || isHsaAccountLoading || isCreating || hasPendingStatus
                }
                onClick={handleSubmit}
              >
                {isLoading || isHsaAccountLoading || isCreating ? <Preloader testId="submit" /> : 'Submit'}
              </AppButton>
            </Box>
          ) : null}
        </Box>
      </Modal>
    </>
  );
};

export default ExcessContributionWithdrawModal;
